<template>
  <b-container>
    <div class="d-flex justify-content-between">
      <h1>Educational Resources</h1>
      <b-btn class="mb-3" variant="secondary" size="sm" to="/resources/create">
        Create Resource
      </b-btn>
    </div>

    <div class="box">
      <h3>Filters</h3>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Skills">
            <b-form-checkbox-group
              name="skills"
              v-model="skills"
              :options="skillOptions"
              :disabled="loading"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="table-responsive mb-4">
        <table class="table b-table table-bordered table-hover">
          <thead class="thead-light">
            <tr>
              <th>Title</th>
              <th>Description</th>
              <th>Resource type</th>
              <th>Skills</th>
              <th>Creator</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="results.length === 0">
              <td class="text-center" colspan="7">
                <em>No resources found!</em>
              </td>
            </tr>

            <tr v-for="resource in results" :key="resource.uuid">
              <td>
                <router-link :to="`resources/${resource.uuid}`">{{
                  resource.title
                }}</router-link>
              </td>
              <td>{{ resource.description }}</td>
              <td>{{ itemTypeTitle(resource.item_type) }}</td>
              <td>{{ skillsText(resource) }}</td>
              <td>
                <router-link :to="`/user/${resource.owner.uuid}`">
                  <img
                    class="rounded-circle img-profile mr-2"
                    :src="resource.owner.avatar"
                    :alt="
                      `${resource.owner.first_name} ${resource.owner.last_name}`
                    "
                  />
                  {{ resource.owner.first_name }} {{ resource.owner.last_name }}
                </router-link>
              </td>
              <td>
                <!-- Action button here, will need dynamic title -->
                <b-btn
                  variant="primary"
                  size="sm"
                  @click="actionButtonTapped(resource)"
                  block
                >
                  {{ actionText(resource.item_type) }}
                </b-btn>

                <!-- Only show an edit or delete buttons when viewed as creator -->
                <b-btn
                  variant="primary"
                  size="sm"
                  block
                  :to="`resources/${resource.uuid}/edit`"
                  v-if="userCreated(resource)"
                  >Edit Resource</b-btn
                >

                <b-btn
                  variant="danger"
                  size="sm"
                  block
                  @click="deleteModel(resource)"
                  v-if="userCreated(resource)"
                >
                  Delete
                </b-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-between align-items-center">
        <b-btn
          variant="secondary"
          size="sm"
          @click="setPage(page - 1)"
          :disabled="loading || page === 1"
        >
          Prev Page
        </b-btn>

        <span class="text-muted" v-if="!loading">
          Page {{ page }} of {{ totalPages }}
        </span>

        <span class="text-muted" v-if="loading">
          Loading
        </span>

        <b-btn
          variant="secondary"
          size="sm"
          @click="setPage(page + 1)"
          :disabled="loading || page === totalPages"
        >
          Next Page
        </b-btn>
      </div>
    </div>
  </b-container>
</template>

<script>
export default {
  name: 'ResourcesList',
  data() {
    return {
      loading: false,
      page: 1,
      totalPages: 0,
      skills: [],
      results: [],
      skillOptions: [
        { text: 'Finance', value: '1' },
        { text: 'Legal', value: '2' },
        { text: 'HR', value: '3' },
        { text: 'Strategy', value: '4' },
        { text: 'Ops', value: '5' },
        { text: 'Tech', value: '6' },
        { text: 'Mktg/Sales', value: '7' },
        { text: 'Buy/Sell', value: '8' },
        { text: 'Franch', value: '9' },
        { text: 'Intl', value: '10' },
        { text: 'Govt', value: '11' },
      ],
    }
  },
  mounted() {
    this.load()

    this.$skillsService.getSkillsAsValues(true)
      .then((skills) => {
        this.skillOptions = skills
      })
  },
  watch: {
    skills() {
      this.page = 1
      this.load()
    },
    // Add debounce and auto search?
    // Add watcher for search text here
  },
  methods: {
    load() {
      this.loading = true
      this.$http
        .get('resources', {
          params: {
            page: this.page,
            page_count: 25,
            sort_key: 'id',
            sort_order: 'DESC',
            skills: this.skills,
          },
        })
        .then(
          (res) => {
            this.results = res.data.data
            this.totalPages = res.data.meta.last_page
          },
          () => {}
        )
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * Returns a more human readable title for EducationalResourceItemTypes
     */
    itemTypeTitle(itemType) {
      switch (itemType) {
        case 'pdf':
        case 'url':
        case 'mp4':
          return itemType.toUpperCase()
        case 'ppt':
          return 'Power Point'
        case 'xls':
          return 'Excel'
        case 'youtube':
          return 'YouTube'
        case 'gdrive':
          return 'Google Drive'
        default:
          return ''
      }
    },
    /**
     * Returns the text to be displayed in the action button
     */
    actionText(itemType) {
      switch (itemType) {
        case 'pdf':
        case 'ppt':
        case 'mp4':
        case 'xls':
          return 'Download'
        case 'youtube':
        case 'gdrive':
        case 'url':
          return 'Open'
        default:
          return ''
      }
    },
    /**
     * Returns a combination of the titles of all associated skills into one string
     */
    skillsText(resource) {
      if (!resource.skills || resource.skills.length == 0) {
        return ''
      }

      return resource.skills.map((s) => s.title.split(' ')[0]).join(', ')
    },
    /**
     * Depending on the resource's item_type, opens the detail view for the provided resource, or opens the detail AND performs the action for the resource itself (downloading or opening)
     */
    actionButtonTapped(resource) {
      const openDetail = this.shouldOpenDetail(resource)
      if (openDetail) {
        this.$router.push({ path: `/resources/${resource.uuid}` })
      } else {
        this.loading = true

        this.$http
          .post(`/resources/${resource.uuid}/trackView`)
          .then(() => {
            this.$router.push({ path: `/resources/${resource.uuid}` })
            window.open(resource.url, '_blank')
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    /**
     * Returns true if the provided resource should open directly to the detail page on action button tap
     */
    shouldOpenDetail(resource) {
      const itemType = resource.item_type

      switch (itemType) {
        case 'pdf':
        case 'ppt':
        case 'url':
        case 'xls':
          return false
        case 'youtube':
        case 'gdrive':
        case 'mp4':
          return true
        default:
          return false
      }
    },
    setPage(newPage) {
      this.page = newPage
      this.load()
      window.scrollTo(0, 0)
    },
    /**
     * Returns true if the user is listed as the owner of the resource. Used to determine display of edit/delete buttons
     */
    userCreated(resource) {
      return resource.owner.uuid == this.$auth.user().uuid
    },
    deleteModel(resource) {
      if (!window.confirm('Are you sure you want to delete this resource?')) {
        return
      }
      this.loading = true

      this.$http
        .delete(`/resources/${resource.uuid}`)
        .then(() => {
          this.load()
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.img-profile {
  width: 24px;
  height: 24px;
}
</style>
